<template>
  <div class="location-request-element-wrapper">
    <div class="element-header">
      <span class="element-type-badge location-badge">
        <i class="fas fa-map-marker-alt"></i>
        <span>Demande de localisation</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <i class="fas fa-copy"></i>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>
    
    <div class="message-content" @click.stop="handleMessageContentClick">
      <!-- Mode affichage du message -->
      <div class="message-display-container" v-if="!isEditingMessage" @click="startEditMessage">
        <div class="message-bubble location-bubble">
          <p class="message-text" v-if="element.message" v-html="formatMessage(element.message)"></p>
          <p class="message-placeholder" v-else>Cliquez pour ajouter un message de demande de localisation...</p>
          
          <!-- Aperçu du bouton de localisation -->
          <div class="location-button-preview">
            <button class="location-btn-preview" disabled>
              <i class="fas fa-map-marker-alt"></i>
              Partager ma localisation
            </button>
          </div>
          
          <div class="message-time">{{ formattedTime }}</div>
        </div>
        <span class="char-count" :class="{'char-count-limit': (element.message || '').length >= 640}">{{ (element.message || '').length }}/640</span>
      </div>
      
      <!-- Mode édition du message -->
      <div v-if="isEditingMessage" class="message-editor-container">
        <!-- Emoji button container au-dessus du textarea -->
        <div class="emoji-button-container">
          <button type="button" class="emoji-button" @click.stop.prevent="$event => toggleEmojiPicker($event)">
            <i class="far fa-smile"></i>
          </button>
          
          <!-- Sélecteur d'emojis -->
          <div v-if="showEmojiPicker" class="emoji-picker">
            <div class="emoji-picker-content">
              <div class="emoji-category">
                <button 
                  v-for="emoji in commonEmojis" 
                  :key="emoji" 
                  type="button"
                  class="emoji-btn" 
                  @mousedown.prevent="(event) => insertEmoji(emoji, event)"
                >
                  {{ emoji }}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Container du textarea avec suggestions de variables -->
        <div class="textarea-container">
          <textarea 
            ref="messageTextarea"
            v-model="localMessage"
            @input="handleMessageInput"
            @keydown="handleKeydown"
            @blur="saveOnBlur"
            placeholder="Tapez votre message de demande de localisation..."
            rows="3"
            maxlength="640"
            class="message-input"
          ></textarea>
          
          <!-- Suggestions de variables -->
          <div v-if="showVariableSuggestions" class="variable-suggestions">
            <div class="suggestions-header">
              <i class="fas fa-code"></i>
              <span>Variables disponibles</span>
            </div>
            <div class="suggestions-list">
              <div 
                v-for="variable in filteredVariableSuggestions" 
                :key="variable.id"
                class="suggestion-item"
                @click="insertVariable(variable)"
              >
                <span class="variable-name">{{ variable.id }}</span>
                <span class="variable-type">{{ variable.type || 'text' }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="editor-footer">
          <div class="editor-actions">
            <button type="button" class="btn-secondary" @click="cancelEditMessage">Annuler</button>
            <button type="button" class="btn-primary" @click="finishEditMessage">Sauvegarder</button>
          </div>
          <span class="char-count" :class="{'char-count-limit': localMessage.length >= 640}">{{ localMessage.length }}/640</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LocationRequestElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isEditingMessage: false,
      localMessage: '',
      showEmojiPicker: false,
      showVariableSuggestions: false,
      variableSuggestionQuery: '',
      boundCloseEmojiPicker: null,
      boundHandleGlobalClick: null,
      commonEmojis: ['😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😆', '😉', '😊', '😋', '😎', '😍', '😘', '🥰', '😗', '😙', '😚', '🙂', '🤗', '🤩', '🤔', '🤨', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '😴', '😌', '😛', '😜', '😝', '🤤', '😒', '😓', '😔', '😕', '🙃', '🤑', '😲', '☹️', '🙁', '😖', '😞', '😟', '😤', '😢', '😭', '😦', '😧', '😨', '😩', '🤯', '😬', '😰', '😱', '🥵', '🥶', '😳', '🤪', '😵', '😡', '😠', '🤬', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '😇', '🥳', '🥴', '🥺', '🤠', '🤡', '🤥', '🤫', '🤭', '🧐', '🤓', '😈', '👿', '👹', '👺', '💀', '👻', '👽', '🤖', '💩', '👍', '👎', '👌', '✌️', '🤞', '🤘', '🤙', '👈', '👉', '☝️', '👆', '👇', '✋', '🤚', '🖐️', '🖖', '👋', '🤟', '✍️', '👏', '👐', '🙌', '🤲', '🙏', '🤝', '💪', '❤️', '💚', '💙', '💜', '🧡', '💛', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '🎉', '🔥', '✨', '🌟', '👏', '💯', '📍', '🗺️', '📌', '🌍', '🌎', '🌏']
    };
  },
  computed: {
    formattedTime() {
      return new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    },
    filteredVariableSuggestions() {
      if (!this.variableSuggestionQuery) {
        return this.variables;
      }
      
      return this.variables.filter(variable => 
        variable.id.toLowerCase().includes(this.variableSuggestionQuery.toLowerCase())
      );
    }
  },
  created() {
    // Créer une version liée de closeEmojiPickerOnClickOutside pour préserver le contexte this
    this.boundCloseEmojiPicker = this.closeEmojiPickerOnClickOutside.bind(this);
    this.boundHandleGlobalClick = this.handleGlobalClick.bind(this);
  },
  beforeUnmount() {
    // Nettoyer les écouteurs d'événements lors de la destruction du composant
    document.removeEventListener('click', this.boundCloseEmojiPicker);
    document.removeEventListener('click', this.boundHandleGlobalClick);
  },
  methods: {
    formatMessage(message) {
      if (!message) return '';
      
      // Remplacer les variables par des spans stylés
      return message.replace(/\{\{([^}]+)\}\}/g, '<span class="variable-highlight">${{$1}}</span>');
    },
    
    handleMessageContentClick(event) {
      event.stopPropagation();
    },
    
    startEditMessage() {
      this.isEditingMessage = true;
      this.localMessage = this.element.message || '';
      this.$nextTick(() => {
        if (this.$refs.messageTextarea) {
          this.$refs.messageTextarea.focus();
        }
      });
      
      // Ajouter les écouteurs d'événements
      document.addEventListener('click', this.boundCloseEmojiPicker);
      document.addEventListener('click', this.boundHandleGlobalClick);
    },
    
    finishEditMessage() {
      if (this.localMessage !== (this.element.message || '')) {
        this.updateElement({ message: this.localMessage });
      }
      this.isEditingMessage = false;
      this.showEmojiPicker = false;
      this.showVariableSuggestions = false;
      
      // Supprimer les écouteurs d'événements
      document.removeEventListener('click', this.boundCloseEmojiPicker);
      document.removeEventListener('click', this.boundHandleGlobalClick);
    },
    
    cancelEditMessage() {
      this.localMessage = this.element.message || '';
      this.isEditingMessage = false;
      this.showEmojiPicker = false;
      this.showVariableSuggestions = false;
      
      // Supprimer les écouteurs d'événements
      document.removeEventListener('click', this.boundCloseEmojiPicker);
      document.removeEventListener('click', this.boundHandleGlobalClick);
    },
    
    handleMessageInput(event) {
      const textarea = event.target;
      const cursorPosition = textarea.selectionStart;
      const textBeforeCursor = this.localMessage.substring(0, cursorPosition);
      
      // Détecter si l'utilisateur tape {{ pour les suggestions de variables
      if (textBeforeCursor.endsWith('{{')) {
        this.showVariableSuggestions = true;
        this.variableSuggestionQuery = '';
      } else if (this.showVariableSuggestions) {
        // Extraire la requête de recherche entre {{ et la position du curseur
        const match = textBeforeCursor.match(/\{\{([^}]*)$/);
        if (match) {
          this.variableSuggestionQuery = match[1];
        } else {
          this.showVariableSuggestions = false;
        }
      }
      
      // Ne plus émettre d'update à chaque frappe - seulement au blur/finish
    },
    
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.cancelEditMessage();
      } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        this.finishEditMessage();
      }
    },
    
    updateElement(changes) {
      const updatedElement = { ...this.element, ...changes };
      this.$emit('update', updatedElement);
    },
    
    toggleEmojiPicker(event) {
      event.stopPropagation();
      event.preventDefault();
      this.showEmojiPicker = !this.showEmojiPicker;
      this.showVariableSuggestions = false;
    },
    
    insertEmoji(emoji, event) {
      event.preventDefault();
      event.stopPropagation();
      
      const textarea = this.$refs.messageTextarea;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        
        this.localMessage = this.localMessage.substring(0, start) + emoji + this.localMessage.substring(end);
        
        this.$nextTick(() => {
          textarea.focus();
          textarea.setSelectionRange(start + emoji.length, start + emoji.length);
        });
      }
      
      this.showEmojiPicker = false;
    },
    
    insertVariable(variable) {
      const textarea = this.$refs.messageTextarea;
      if (textarea) {
        const cursorPosition = textarea.selectionStart;
        const textBeforeCursor = this.localMessage.substring(0, cursorPosition);
        const textAfterCursor = this.localMessage.substring(cursorPosition);
        
        // Trouver le début de {{ pour remplacer
        const match = textBeforeCursor.match(/\{\{([^}]*)$/);
        if (match) {
          const startPos = cursorPosition - match[0].length;
          const variableText = `{{${variable.id}}}`;
          
          this.localMessage = this.localMessage.substring(0, startPos) + variableText + textAfterCursor;
          
          this.$nextTick(() => {
            textarea.focus();
            textarea.setSelectionRange(startPos + variableText.length, startPos + variableText.length);
          });
        }
      }
      
      this.showVariableSuggestions = false;
    },
    
    closeEmojiPickerOnClickOutside(event) {
      if (!event.target.closest('.emoji-picker') && !event.target.closest('.emoji-button')) {
        this.showEmojiPicker = false;
      }
    },
    
    handleGlobalClick(event) {
      if (!event.target.closest('.variable-suggestions') && !event.target.closest('.message-input')) {
        this.showVariableSuggestions = false;
      }
    },
    
    saveOnBlur() {
      // Sauvegarder seulement si le contenu a changé et qu'on ne clique pas sur les boutons d'action
      if (this.localMessage !== (this.element.message || '')) {
        this.updateElement({ message: this.localMessage });
      }
    }
  }
};
</script>

<style scoped>
.location-request-element-wrapper {
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  overflow: hidden;
  transition: all 0.2s ease;
}

.location-request-element-wrapper:hover {
  border-color: #cbd5e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
}

.location-badge {
  color: #e53e3e;
}

.location-badge i {
  color: #e53e3e;
}

.element-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  background: transparent;
  border: none;
  color: #718096;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: #e2e8f0;
  color: #4a5568;
}

.delete-btn:hover {
  background: #fed7d7;
  color: #e53e3e;
}

.message-content {
  padding: 16px;
}

.message-display-container {
  cursor: pointer;
}

.message-bubble {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 8px;
  position: relative;
}

.location-bubble {
  border-left: 4px solid #e53e3e;
}

.message-text {
  margin: 0;
  color: #2d3748;
  line-height: 1.5;
  word-wrap: break-word;
}

.message-placeholder {
  margin: 0;
  color: #a0aec0;
  font-style: italic;
}

.location-button-preview {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.location-btn-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #e53e3e;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: not-allowed;
  opacity: 0.8;
}

.location-btn-preview i {
  font-size: 16px;
}

.message-time {
  font-size: 12px;
  color: #a0aec0;
  margin-top: 8px;
}

.char-count {
  font-size: 12px;
  color: #718096;
  text-align: right;
}

.char-count-limit {
  color: #e53e3e;
  font-weight: 500;
}

.message-editor-container {
  position: relative;
}

.emoji-button-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.emoji-button {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  color: #718096;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.emoji-button:hover {
  background: #edf2f7;
  color: #4a5568;
}

.emoji-picker {
  position: absolute;
  top: 40px;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  width: 280px;
  max-height: 200px;
  overflow-y: auto;
}

.emoji-picker-content {
  padding: 12px;
}

.emoji-category {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
}

.emoji-btn {
  background: transparent;
  border: none;
  padding: 6px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.2s ease;
}

.emoji-btn:hover {
  background: #f7fafc;
}

.textarea-container {
  position: relative;
}

.message-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

.message-input:focus {
  outline: none;
  border-color: #e53e3e;
  box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
}

.variable-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
}

.suggestions-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  font-weight: 500;
  color: #4a5568;
}

.suggestions-list {
  padding: 4px 0;
}

.suggestion-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.suggestion-item:hover {
  background: #f7fafc;
}

.variable-name {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  color: #2d3748;
  font-weight: 500;
}

.variable-type {
  font-size: 11px;
  color: #718096;
  background: #edf2f7;
  padding: 2px 6px;
  border-radius: 10px;
}

.editor-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
}

.editor-actions {
  display: flex;
  gap: 8px;
}

.btn-secondary,
.btn-primary {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #4a5568;
}

.btn-secondary:hover {
  background: #f7fafc;
}

.btn-primary {
  background: #e53e3e;
  border: 1px solid #e53e3e;
  color: white;
}

.btn-primary:hover {
  background: #c53030;
  border-color: #c53030;
}

.variable-highlight {
  background: #edf2f7;
  color: #2d3748;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
  font-weight: 500;
}
</style> 